import { ActionCreators } from "redux-undo";
import { importStateAsync, selectBox, selectNavItem } from "./actions.es6";
import { getEditorPlayhead, seekEditorPlayhead } from "./playhead_tracker.es6";
import { getBaseUpdatedAt, setBaseUpdatedAt } from "./saveConflict.es6";
import { FPS, getSceneDurationSec } from "./sceneTimeline.es6";

// Everything the editor keeps (selected scene, selected boxes, boxes, toolbars) lives in
// state.undoGroup.present; the top level of the store only holds non-undoable slices.
const present = (store) => store.getState().undoGroup.present;

// "none selected" is -1 in the box_selected / box_selected_all reducers.
function selectedIds(p) {
  const all = p.boxSelectedAll;
  if (Array.isArray(all) && all.length) return all;
  return typeof p.boxSelected === "string" ? [p.boxSelected] : [];
}

const sceneTitle = (p, navId) =>
  p.viewToolbarsById?.[navId]?.viewName || p.navItemsById?.[navId]?.name;

export function getContext(store) {
  const p = present(store);
  const navId = p.navItemSelected;
  const playhead = getEditorPlayhead();
  return {
    trainingId: window.idealStudioTrainingId,
    navId,
    sceneTitle: sceneTitle(p, navId),
    // On a scene change the store updates before the timeline remounts: the playhead
    // still belongs to the previous scene until its sceneId catches up.
    playheadSec: playhead && String(playhead.sceneId) === String(navId) ? playhead.frame / FPS : 0,
    selectedBoxIds: selectedIds(p),
  };
}

// Box geometry is stored as "12.5%" of the 1920x1080 canvas (or a plain px number).
const CANVAS = { w: 1920, h: 1080 };
function toPx(v, unit, base) {
  const n = parseFloat(v);
  if (!Number.isFinite(n)) return undefined;
  const isPct = typeof v === "string" ? v.trim().endsWith("%") : unit === "%";
  return Math.round(isPct ? (n / 100) * base : n);
}

function plainText(html) {
  let h = String(html);
  try {
    h = decodeURI(h);
  } catch (e) {
    /* not URI-encoded */
  }
  return (new DOMParser().parseFromString(h, "text/html").body.textContent || "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 500);
}

function getScene(store, navId) {
  const p = present(store);
  const id = navId ?? p.navItemSelected;
  const nav = p.navItemsById?.[id];
  if (!nav) return fail(`Unknown navId: ${id}`);
  const boxes = (nav.boxes || [])
    .filter((boxId) => p.boxesById?.[boxId])
    .map((boxId) => {
      const box = p.boxesById[boxId];
      const tb = p.pluginToolbarsById?.[boxId] || {};
      const st = tb.structure || {};
      const tl = box.timeline || {};
      const state = tb.state || {};
      const out = {
        boxId,
        type: tb.pluginId,
        x: toPx(box.position?.x, undefined, CANVAS.w),
        y: toPx(box.position?.y, undefined, CANVAS.h),
        w: toPx(st.width, st.widthUnit, CANVAS.w),
        h: toPx(st.height, st.heightUnit, CANVAS.h),
        startSec: (tl.startFrame || 0) / FPS,
        endSec: (tl.endFrame || 0) / FPS,
        visible: (tl.visible !== undefined ? tl.visible : box.visible) !== false,
      };
      const text = state.__text ?? state.text;
      if (typeof text === "string" && text) out.text = plainText(text);
      if (typeof state.url === "string" && state.url) out.url = state.url;
      return out;
    });
  return {
    navId: id,
    title: sceneTitle(p, id),
    durationSec: getSceneDurationSec(id, p),
    boxes,
  };
}

// Re-fetch the training (like EditorApp._reloadServerCopy), then reopen the scene and
// playhead second that were open. importStateAsync swallows its own errors, so success
// is detected by the response callback having run.
async function reload(store) {
  const before = present(store);
  const prevNav = before.navItemSelected;
  const prevSec = (getEditorPlayhead()?.frame || 0) / FPS;
  let loaded = false;
  try {
    await store.dispatch(
      importStateAsync(window.idealStudioTrainingId, window.tenantId, (r) => {
        loaded = true;
        setBaseUpdatedAt(r.updatedAt);
      })
    );
    // The callback runs BEFORE the thunk validates/dispatches IMPORT_STATE, so it alone
    // proves nothing: the imported state must also have replaced `present`.
    if (!loaded || present(store) === before) return fail("Reload failed");
    window.aideaMarkSaved?.(); // server copy is the new clean baseline (aideaDirty)
    if (present(store).navItemsById?.[prevNav]) {
      store.dispatch(selectNavItem(prevNav));
      if (prevSec > 0 && !(await seekEditorPlayhead(prevNav, prevSec)))
        return fail("Reloaded, but the scene could not be reopened");
    }
    return OK;
  } catch (e) {
    return fail(errText(e));
  }
}

const OK = { ok: true };
const fail = (error) => ({ ok: false, error });
const errText = (e) => String((e && e.message) || e);

// Resolves only after the save finished (ideaEnvSaveState calls back after the PUT).
function save(store) {
  return new Promise((resolve) => {
    const state = store.getState();
    try {
      window.ideaEnvSaveState(
        state,
        false,
        () => {
          window.aideaMarkSaved?.(state); // aideaDirty: only edits made after this save count
          resolve({ ok: true, baseUpdatedAt: getBaseUpdatedAt() });
        },
        (err) =>
          resolve(
            err && err.isSaveConflict
              ? {
                  ok: false,
                  conflict: true,
                  error: "Save conflict",
                  currentUpdatedAt: err.currentUpdatedAt,
                }
              : err && err.buildLocked
                ? fail("Training is locked by a running build")
                : fail(errText(err) || "Save failed")
          )
      );
    } catch (e) {
      resolve(fail(errText(e)));
    }
  });
}

function goToScene(store, navId) {
  if (!present(store).navItemsById?.[navId]) {
    return fail(`Unknown navId: ${navId}`);
  }
  store.dispatch(selectNavItem(navId));
  return present(store).navItemSelected === navId ? OK : fail(`Could not open scene ${navId}`);
}

// A box of another scene is selectable only once its scene is open.
function selectBoxById(store, boxId) {
  const p = present(store);
  const box = p.boxesById?.[boxId];
  if (!box) return fail(`Unknown boxId: ${boxId}`);
  const owner = Object.keys(p.navItemsById || {}).find((n) =>
    p.navItemsById[n].boxes?.includes(boxId)
  );
  if (owner !== undefined && owner !== p.navItemSelected) store.dispatch(selectNavItem(owner));
  store.dispatch(selectBox(boxId, box));
  return selectedIds(present(store)).includes(boxId) ? OK : fail(`Could not select box ${boxId}`);
}

// IdeaTimeline's "aidea-timeline-play" event toggles play/pause, so only fire it on a state change.
function setPlaying(want) {
  const p = getEditorPlayhead();
  if (!p) return fail("Timeline not mounted");
  if (!!p.playing !== want) document.dispatchEvent(new CustomEvent("aidea-timeline-play"));
  // The timeline flips its playing ref synchronously inside the event handler.
  const now = getEditorPlayhead();
  if (!now || !!now.playing !== want)
    return fail(want ? "Could not start playback" : "Could not pause");
  return { ok: true, playing: want, advancing: !!now.advancing };
}

// Clamped to [0, scene duration]. Uses the selected scene from undoGroup.present: passing
// undefined to seekEditorPlayhead made it dispatch selectNavItem(undefined) -> Redux error #14.
async function seek(store, sec) {
  if (sec === null || sec === "" || !Number.isFinite(Number(sec)))
    return fail("sec must be a number");
  try {
    const p = present(store);
    const navId = p.navItemSelected;
    if (navId === undefined || navId === null) return fail("No scene is open");
    const dur = getSceneDurationSec(navId, p); // 0 = unknown length: only clamp below
    const clamped = Math.max(0, dur > 0 ? Math.min(Number(sec), dur) : Number(sec));
    const done = await seekEditorPlayhead(navId, clamped);
    return done ? { ok: true, sec: clamped } : fail("Timeline not mounted");
  } catch (e) {
    return fail(errText(e));
  }
}

// Older Chrome passed tool args as a JSON string.
const parseArgs = (a) => {
  if (typeof a !== "string") return a || {};
  try {
    return JSON.parse(a) || {};
  } catch (e) {
    return {};
  }
};

const obj = (properties = {}, required = []) => ({ type: "object", properties, required });

// WebMCP (Chrome origin trial) is still changing: every registration is isolated and optional.
export function registerWebMcpTools() {
  const mc = document.modelContext || navigator.modelContext;
  if (!mc || typeof mc.registerTool !== "function") return;
  const b = () => window.aideaBridge;
  const tools = [
    [
      "aidea_get_context",
      "Get the open scene, its title, the playhead seconds and the selected box ids.",
      obj(),
      (a) => b().getContext(),
      { readOnlyHint: true },
    ],
    [
      "aidea_reload",
      "Reload the training from the server, keeping the open scene.",
      obj(),
      (a) => b().reload(),
    ],
    [
      "aidea_save",
      "Save the training to the server; resolves { ok: true }, or { ok: false, conflict: true } on a version conflict.",
      obj(),
      (a) => b().save(),
      { consequentialHint: true },
    ],
    [
      "aidea_go_to_scene",
      "Open the scene with the given navId in the editor.",
      obj({ navId: { type: "string" } }, ["navId"]),
      (a) => b().goToScene(a.navId),
    ],
    [
      "aidea_get_scene",
      "Describe a scene (default: the open one): title, duration and every box with type, x/y/w/h px on the 1920x1080 canvas, start/end seconds, visibility, text or url.",
      obj({ navId: { type: "string" } }),
      (a) => b().getScene(a.navId),
      { readOnlyHint: true },
    ],
    ["aidea_play", "Start timeline playback of the open scene.", obj(), (a) => b().play()],
    ["aidea_pause", "Pause timeline playback.", obj(), (a) => b().pause()],
    [
      "aidea_seek",
      "Move the playhead of the open scene to the given second.",
      obj({ sec: { type: "number" } }, ["sec"]),
      (a) => b().seek(a.sec),
    ],
    [
      "aidea_select_box",
      "Select the box (element) with the given boxId on the canvas.",
      obj({ boxId: { type: "string" } }, ["boxId"]),
      (a) => b().selectBox(a.boxId),
    ],
  ];
  tools.forEach(([name, description, inputSchema, run, annotations]) => {
    try {
      mc.registerTool({
        name,
        description,
        inputSchema,
        ...(annotations && { annotations }),
        // { ok:false } / { conflict } results are errors for the caller, not silent successes.
        execute: async (args) => {
          let res;
          try {
            res = await run(parseArgs(args));
          } catch (e) {
            res = fail(errText(e));
          }
          const isError = !!res && (res.ok === false || res.conflict === true);
          return {
            content: [{ type: "text", text: JSON.stringify(res) }],
            ...(isError && { isError }),
          };
        },
      });
    } catch (e) {
      /* API in flux: never break the editor */
    }
  });
}

export function installAideaBridge(store) {
  window.aideaBridge = {
    reload: () => reload(store),
    save: () => save(store),
    getContext: () => getContext(store),
    getScene: (navId) => getScene(store, navId),
    getBaseUpdatedAt: () => getBaseUpdatedAt(),
    isDirty: () => !!window.aideaDirty,
    goToScene: (navId) => goToScene(store, navId),
    play: () => setPlaying(true),
    pause: () => setPlaying(false),
    seek: (sec) => seek(store, sec),
    selectBox: (boxId) => selectBoxById(store, boxId),
  };
  registerWebMcpTools();
  const emit = () =>
    window.dispatchEvent(new CustomEvent("aidea:context", { detail: getContext(store) }));
  let last = "";
  store.subscribe(() => {
    const s = present(store);
    const key = `${s.navItemSelected}|${JSON.stringify(s.boxSelectedAll)}|${s.boxSelected}`;
    if (key !== last) {
      last = key;
      emit();
    }
  });
  // Playhead lives outside redux: seek/play events from the timeline, throttled.
  let timer = null;
  const throttled = () => {
    if (timer) return;
    timer = setTimeout(() => {
      timer = null;
      emit();
    }, 500);
  };
  document.addEventListener("aidea-timeline-seek", throttled);
  document.addEventListener("aidea-timeline-status", throttled);
}
